import React, { useEffect, useState } from 'react';
import { updateContent } from '../../services/api';
import { useSite } from '../../context/SiteContext';
import { useToast } from '../../hooks/use-toast';
import { Save, Loader2, Plus, Trash2, Sparkles, Send, Mail } from 'lucide-react';
import ImageUpload from './ImageUpload';
import { I18nInput, bulkTranslate } from './I18nInput';
import { adminTestEmail } from '../../services/api';

const SectionTitle = ({ children, sub }) => (
  <div className="mb-5">
    <h2 className="font-display text-white text-2xl font-light">{children}</h2>
    {sub && <p className="text-white/40 text-sm mt-1">{sub}</p>}
  </div>
);

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">{label}</span>
    {children}
  </label>
);

const Tab = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
      active
        ? 'bg-[#00e0c7]/10 text-[#00e0c7] border border-[#00e0c7]/30'
        : 'text-white/60 hover:text-white border border-transparent'
    }`}
  >
    {children}
  </button>
);

const AdminContent = () => {
  const { content, reload } = useSite();
  const { toast } = useToast();
  const [tab, setTab] = useState('hero');
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (content) setData(JSON.parse(JSON.stringify(content)));
  }, [content]);

  if (!data) {
    return <div className="text-white/50">Cargando...</div>;
  }

  const save = async (payload) => {
    setSaving(true);
    try {
      await updateContent(payload);
      await reload();
      toast({ title: 'Guardado', description: 'Los cambios se han aplicado a la web.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar.' });
    } finally {
      setSaving(false);
    }
  };

  // helpers
  const setHero = (k, v) => setData({ ...data, hero: { ...data.hero, [k]: v } });
  const setAbout = (k, v) => setData({ ...data, about: { ...data.about, [k]: v } });
  const setContact = (k, v) => setData({ ...data, contact: { ...data.contact, [k]: v } });
  const setBranding = (k, v) => setData({ ...data, branding: { ...(data.branding || {}), [k]: v } });
  const setLegal = (k, v) => setData({ ...data, legal: { ...(data.legal || {}), [k]: v } });
  const setNotif = (k, v) => setData({ ...data, notifications: { ...(data.notifications || {}), [k]: v } });
  const setImpactSec = (k, v) => setData({ ...data, impactSection: { ...(data.impactSection || {}), [k]: v } });
  const setContactSec = (k, v) => setData({ ...data, contactSection: { ...(data.contactSection || {}), [k]: v } });

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      await adminTestEmail();
      toast({ title: '✉️ Email de prueba enviado', description: `Revisa la bandeja de entrada de ${data.notifications?.leadsRecipient || 'tu correo'}.` });
    } catch (e) {
      toast({ title: 'Error al enviar', description: e.response?.data?.detail || 'No se pudo enviar el email de prueba.' });
    } finally {
      setSendingTest(false);
    }
  };

  // Bulk translate helper for a specific section
  const translateSection = async (collect, applyAll) => {
    setTranslating(true);
    try {
      await bulkTranslate(collect(), toast);
      // applyAll is no-op since items in collect() reference state via set; but we want a re-render.
      setData({ ...data });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">Contenido</div>
        <h1 className="font-display text-4xl text-white font-light">Contenido Web</h1>
        <p className="text-white/50 mt-2 text-sm">Cada texto editable tiene versión ES y EN. Pulsa <span className="text-[#9be8dc]">IA</span> para traducir un campo o usa el botón <span className="text-[#9be8dc]">Traducir sección</span>.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-white/8">
        <Tab active={tab === 'branding'} onClick={() => setTab('branding')}>Logo & Marca</Tab>
        <Tab active={tab === 'hero'} onClick={() => setTab('hero')}>Hero</Tab>
        <Tab active={tab === 'stats'} onClick={() => setTab('stats')}>Cifras</Tab>
        <Tab active={tab === 'guarantees'} onClick={() => setTab('guarantees')}>Garantías</Tab>
        <Tab active={tab === 'division'} onClick={() => setTab('division')}>División</Tab>
        <Tab active={tab === 'about'} onClick={() => setTab('about')}>Quiénes Somos</Tab>
        <Tab active={tab === 'contact'} onClick={() => setTab('contact')}>Contacto</Tab>
        <Tab active={tab === 'form'} onClick={() => setTab('form')}>Opciones de formulario</Tab>
        <Tab active={tab === 'legal'} onClick={() => setTab('legal')}>Datos Legales</Tab>
        <Tab active={tab === 'notifications'} onClick={() => setTab('notifications')}>Notificaciones Email</Tab>
      </div>

      {/* BRANDING - no localization (logo/name/tagline are brand) */}
      {tab === 'branding' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <SectionTitle sub="Logo, nombre y tagline que aparecen en el header y footer">
            Logo y Marca
          </SectionTitle>
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <ImageUpload
                label="Logo de la empresa (máx 5MB)"
                value={data.branding?.logoUrl || ''}
                onChange={(url) => setBranding('logoUrl', url)}
                aspect="square"
              />
            </div>
            <div className="space-y-5">
              <Field label="Nombre de la empresa">
                <input className="ldc-input" value={data.branding?.companyName || ''} onChange={(e) => setBranding('companyName', e.target.value)} />
              </Field>
              <Field label="Tagline">
                <input className="ldc-input" value={data.branding?.tagline || ''} onChange={(e) => setBranding('tagline', e.target.value)} />
              </Field>
            </div>
          </div>
          <SaveBar saving={saving} onSave={() => save({ branding: data.branding })} />
        </div>
      )}

      {/* HERO */}
      {tab === 'hero' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2 className="font-display text-white text-2xl font-light">Hero Section</h2>
              <p className="text-white/40 text-sm mt-1">Banner principal con vídeo de fondo</p>
            </div>
            <TranslateSectionBtn
              translating={translating}
              onClick={() => translateSection(() => [
                { value: data.hero.badge, set: (v) => setHero('badge', v) },
                { value: data.hero.title, set: (v) => setHero('title', v) },
                { value: data.hero.titleAccent, set: (v) => setHero('titleAccent', v) },
                { value: data.hero.subtitle, set: (v) => setHero('subtitle', v) },
                { value: data.hero.primaryCta, set: (v) => setHero('primaryCta', v) },
                { value: data.hero.secondaryCta, set: (v) => setHero('secondaryCta', v) },
              ])}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><I18nInput label="Badge" value={data.hero.badge} onChange={(v) => setHero('badge', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Título (línea 1)" value={data.hero.title} onChange={(v) => setHero('title', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Título en italica (acento)" value={data.hero.titleAccent} onChange={(v) => setHero('titleAccent', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Subtítulo" value={data.hero.subtitle} onChange={(v) => setHero('subtitle', v)} multiline rows={3} /></div>
            <I18nInput label="CTA Principal" value={data.hero.primaryCta} onChange={(v) => setHero('primaryCta', v)} />
            <I18nInput label="CTA Secundario" value={data.hero.secondaryCta} onChange={(v) => setHero('secondaryCta', v)} />
            <Field label="URL Vídeo MP4" full><input className="ldc-input" value={data.hero.videoUrl} onChange={(e) => setHero('videoUrl', e.target.value)} /></Field>
            <Field label="Imagen de fallback (URL)" full><input className="ldc-input" value={data.hero.fallbackImage} onChange={(e) => setHero('fallbackImage', e.target.value)} /></Field>
          </div>
          <SaveBar saving={saving} onSave={() => save({ hero: data.hero })} />
        </div>
      )}

      {/* STATS */}
      {tab === 'stats' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>Cifras de Impacto</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => {
              const items = [
                { value: data.impactSection?.eyebrow, set: (v) => setImpactSec('eyebrow', v) },
                { value: data.impactSection?.title, set: (v) => setImpactSec('title', v) },
              ];
              data.impactStats.forEach((s, i) => {
                items.push({ value: s.suffix, set: (v) => updateArrItem('impactStats', i, 'suffix', v) });
                items.push({ value: s.label, set: (v) => updateArrItem('impactStats', i, 'label', v) });
              });
              return items;
            })} />
          </div>

          {/* Encabezado de la sección */}
          <div className="mb-6 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <h3 className="text-white text-sm font-medium mb-3">Encabezado de la sección</h3>
            <div className="space-y-4">
              <I18nInput label="Etiqueta superior (eyebrow)" value={data.impactSection?.eyebrow} onChange={(v) => setImpactSec('eyebrow', v)} />
              <I18nInput label="Título principal" value={data.impactSection?.title} onChange={(v) => setImpactSec('title', v)} multiline rows={2} />
            </div>
          </div>

          <h3 className="text-white text-sm font-medium mb-3">Ítems numéricos</h3>
          <div className="space-y-4">
            {data.impactStats.map((item, idx) => (
              <ItemCard key={idx} index={idx} onRemove={() => removeArrItem('impactStats', idx)}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Valor (sin traducción)"><input className="ldc-input" placeholder="+10" value={item.value || ''} onChange={(e) => updateArrItem('impactStats', idx, 'value', e.target.value)} /></Field>
                  <I18nInput label="Sufijo" placeholder="Años" value={item.suffix} onChange={(v) => updateArrItem('impactStats', idx, 'suffix', v)} />
                  <div className="sm:col-span-2"><I18nInput label="Etiqueta" value={item.label} onChange={(v) => updateArrItem('impactStats', idx, 'label', v)} /></div>
                </div>
              </ItemCard>
            ))}
          </div>
          <AddBtn onClick={() => addArrItem('impactStats', { value: '', suffix: { es: '', en: '' }, label: { es: '', en: '' } })} />
          <SaveBar saving={saving} onSave={() => save({ impactSection: data.impactSection, impactStats: data.impactStats })} />
        </div>
      )}

      {/* GUARANTEES */}
      {tab === 'guarantees' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>Garantías Técnicas</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => {
              const items = [];
              data.guarantees.forEach((g, i) => {
                items.push({ value: g.title, set: (v) => updateArrItem('guarantees', i, 'title', v) });
                items.push({ value: g.text, set: (v) => updateArrItem('guarantees', i, 'text', v) });
              });
              return items;
            })} />
          </div>
          <div className="space-y-4">
            {data.guarantees.map((item, idx) => (
              <ItemCard key={idx} index={idx} onRemove={() => removeArrItem('guarantees', idx)}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Icono (ShieldCheck, Headphones, BadgeCheck, Zap...)"><input className="ldc-input" value={item.icon || ''} onChange={(e) => updateArrItem('guarantees', idx, 'icon', e.target.value)} /></Field>
                  <I18nInput label="Título" value={item.title} onChange={(v) => updateArrItem('guarantees', idx, 'title', v)} />
                  <div className="sm:col-span-2"><I18nInput label="Descripción breve" value={item.text} onChange={(v) => updateArrItem('guarantees', idx, 'text', v)} multiline rows={2} /></div>
                </div>
              </ItemCard>
            ))}
          </div>
          <AddBtn onClick={() => addArrItem('guarantees', { icon: 'ShieldCheck', title: { es: '', en: '' }, text: { es: '', en: '' } })} />
          <SaveBar saving={saving} onSave={() => save({ guarantees: data.guarantees })} />
        </div>
      )}

      {/* DIVISION */}
      {tab === 'division' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>División Operativa</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => {
              const items = [];
              data.divisionBlocks.forEach((b, i) => {
                items.push({ value: b.tag, set: (v) => updateArrItem('divisionBlocks', i, 'tag', v) });
                items.push({ value: b.title, set: (v) => updateArrItem('divisionBlocks', i, 'title', v) });
                items.push({ value: b.description, set: (v) => updateArrItem('divisionBlocks', i, 'description', v) });
                (b.bullets || []).forEach((blt, bi) => {
                  items.push({ value: blt, set: (v) => {
                    const arr = [...(b.bullets || [])]; arr[bi] = v;
                    updateArrItem('divisionBlocks', i, 'bullets', arr);
                  }});
                });
              });
              return items;
            })} />
          </div>
          <div className="space-y-4">
            {data.divisionBlocks.map((item, idx) => (
              <ItemCard key={idx} index={idx} onRemove={() => removeArrItem('divisionBlocks', idx)}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="ID (digital, fisico)"><input className="ldc-input" value={item.id || ''} onChange={(e) => updateArrItem('divisionBlocks', idx, 'id', e.target.value)} /></Field>
                  <I18nInput label="Tag" value={item.tag} onChange={(v) => updateArrItem('divisionBlocks', idx, 'tag', v)} />
                  <div className="sm:col-span-2"><I18nInput label="Título" value={item.title} onChange={(v) => updateArrItem('divisionBlocks', idx, 'title', v)} /></div>
                  <div className="sm:col-span-2"><I18nInput label="Descripción" value={item.description} onChange={(v) => updateArrItem('divisionBlocks', idx, 'description', v)} multiline rows={3} /></div>
                  <Field label="Imagen URL" full><input className="ldc-input" value={item.image || ''} onChange={(e) => updateArrItem('divisionBlocks', idx, 'image', e.target.value)} /></Field>
                  <div className="sm:col-span-2">
                    <BulletsEditor
                      bullets={item.bullets || []}
                      onChange={(arr) => updateArrItem('divisionBlocks', idx, 'bullets', arr)}
                    />
                  </div>
                </div>
              </ItemCard>
            ))}
          </div>
          <AddBtn onClick={() => addArrItem('divisionBlocks', { id: 'nuevo', tag: { es: '', en: '' }, title: { es: '', en: '' }, description: { es: '', en: '' }, bullets: [], image: '' })} />
          <SaveBar saving={saving} onSave={() => save({ divisionBlocks: data.divisionBlocks })} />
        </div>
      )}

      {/* ABOUT */}
      {tab === 'about' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>Quiénes Somos</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => {
              const items = [
                { value: data.about.title, set: (v) => setAbout('title', v) },
                { value: data.about.titleAccent, set: (v) => setAbout('titleAccent', v) },
                { value: data.about.intro, set: (v) => setAbout('intro', v) },
                { value: data.about.mission, set: (v) => setAbout('mission', v) },
              ];
              (data.about.pillars || []).forEach((p, i) => {
                items.push({ value: p.title, set: (v) => updatePillar(i, 'title', v) });
                items.push({ value: p.text, set: (v) => updatePillar(i, 'text', v) });
              });
              return items;
            })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><I18nInput label="Título principal" value={data.about.title} onChange={(v) => setAbout('title', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Acento itálico (final del título, con espacio)" value={data.about.titleAccent} onChange={(v) => setAbout('titleAccent', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Introducción" value={data.about.intro} onChange={(v) => setAbout('intro', v)} multiline rows={5} /></div>
            <div className="sm:col-span-2"><I18nInput label="Misión" value={data.about.mission} onChange={(v) => setAbout('mission', v)} multiline rows={3} /></div>
            <Field label="Imagen URL" full><input className="ldc-input" value={data.about.image || ''} onChange={(e) => setAbout('image', e.target.value)} /></Field>
          </div>

          <div className="mt-8">
            <h3 className="text-white text-sm font-medium mb-3">Pilares</h3>
            <div className="space-y-3">
              {(data.about.pillars || []).map((p, i) => (
                <ItemCard key={i} index={i} onRemove={() => {
                  const arr = (data.about.pillars || []).filter((_, j) => j !== i);
                  setAbout('pillars', arr);
                }}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Número"><input className="ldc-input" placeholder="01" value={p.number || ''} onChange={(e) => updatePillar(i, 'number', e.target.value)} /></Field>
                    <I18nInput label="Título" value={p.title} onChange={(v) => updatePillar(i, 'title', v)} />
                    <div className="sm:col-span-2"><I18nInput label="Texto" value={p.text} onChange={(v) => updatePillar(i, 'text', v)} multiline rows={2} /></div>
                  </div>
                </ItemCard>
              ))}
            </div>
            <button onClick={() => setAbout('pillars', [...(data.about.pillars || []), { number: '0X', title: { es: '', en: '' }, text: { es: '', en: '' } }])} className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white hover:border-[#00e0c7]/40 text-sm transition-colors">
              <Plus className="w-4 h-4" /> Añadir pilar
            </button>
          </div>

          <SaveBar saving={saving} onSave={() => save({ about: data.about })} />
        </div>
      )}

      {/* CONTACT */}
      {tab === 'contact' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>Datos de Contacto</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => [
              { value: data.contactSection?.eyebrow, set: (v) => setContactSec('eyebrow', v) },
              { value: data.contactSection?.title, set: (v) => setContactSec('title', v) },
              { value: data.contactSection?.description, set: (v) => setContactSec('description', v) },
              { value: data.contactSection?.submitLabel, set: (v) => setContactSec('submitLabel', v) },
              { value: data.contactSection?.volumeLabel, set: (v) => setContactSec('volumeLabel', v) },
              { value: data.contact.address, set: (v) => setContact('address', v) },
              { value: data.contact.hours, set: (v) => setContact('hours', v) },
            ])} />
          </div>

          {/* Encabezado de la sección */}
          <div className="mb-6 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            <h3 className="text-white text-sm font-medium mb-3">Encabezado de la sección</h3>
            <div className="space-y-4">
              <I18nInput label="Etiqueta superior (eyebrow)" value={data.contactSection?.eyebrow} onChange={(v) => setContactSec('eyebrow', v)} />
              <I18nInput label="Título principal" value={data.contactSection?.title} onChange={(v) => setContactSec('title', v)} multiline rows={2} />
              <I18nInput label="Descripción debajo del título" value={data.contactSection?.description} onChange={(v) => setContactSec('description', v)} multiline rows={2} />
              <I18nInput label="Texto del botón de envío" value={data.contactSection?.submitLabel} onChange={(v) => setContactSec('submitLabel', v)} />
              <I18nInput label="Etiqueta del desplegable de volumen de salas" value={data.contactSection?.volumeLabel} onChange={(v) => setContactSec('volumeLabel', v)} />
            </div>
          </div>

          <h3 className="text-white text-sm font-medium mb-3">Datos de contacto mostrados</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Email"><input className="ldc-input" value={data.contact.email || ''} onChange={(e) => setContact('email', e.target.value)} /></Field>
            <Field label="Teléfono"><input className="ldc-input" value={data.contact.phone || ''} onChange={(e) => setContact('phone', e.target.value)} /></Field>
            <div className="sm:col-span-2"><I18nInput label="Dirección" value={data.contact.address} onChange={(v) => setContact('address', v)} /></div>
            <div className="sm:col-span-2"><I18nInput label="Horario" value={data.contact.hours} onChange={(v) => setContact('hours', v)} /></div>
          </div>
          <SaveBar saving={saving} onSave={() => save({ contactSection: data.contactSection, contact: data.contact })} />
        </div>
      )}

      {/* FORM OPTIONS */}
      {tab === 'form' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <SectionTitle>Opciones del formulario</SectionTitle>
            <TranslateSectionBtn translating={translating} onClick={() => translateSection(() => {
              const items = [];
              (data.formOptions?.serviceOptions || []).forEach((o, i) => {
                items.push({ value: o, set: (v) => {
                  const arr = [...(data.formOptions.serviceOptions || [])];
                  arr[i] = v;
                  setData({ ...data, formOptions: { ...data.formOptions, serviceOptions: arr } });
                }});
              });
              (data.formOptions?.volumeOptions || []).forEach((o, i) => {
                items.push({ value: o, set: (v) => {
                  const arr = [...(data.formOptions.volumeOptions || [])];
                  arr[i] = v;
                  setData({ ...data, formOptions: { ...data.formOptions, volumeOptions: arr } });
                }});
              });
              return items;
            })} />
          </div>
          <h3 className="text-white text-sm font-medium mb-3">Tipos de Servicio</h3>
          <div className="space-y-3 mb-6">
            {(data.formOptions?.serviceOptions || []).map((o, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1"><I18nInput value={o} onChange={(v) => {
                  const arr = [...(data.formOptions.serviceOptions || [])];
                  arr[i] = v;
                  setData({ ...data, formOptions: { ...data.formOptions, serviceOptions: arr } });
                }} /></div>
                <button onClick={() => {
                  const arr = (data.formOptions.serviceOptions || []).filter((_, j) => j !== i);
                  setData({ ...data, formOptions: { ...data.formOptions, serviceOptions: arr } });
                }} className="p-2 mb-0 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setData({ ...data, formOptions: { ...data.formOptions, serviceOptions: [...(data.formOptions?.serviceOptions || []), { es: '', en: '' }] } })} className="inline-flex items-center gap-2 px-4 h-9 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white text-xs transition-colors">
              <Plus className="w-3 h-3" /> Añadir opción
            </button>
          </div>
          <h3 className="text-white text-sm font-medium mb-3">Volúmenes de Salas</h3>
          <div className="space-y-3">
            {(data.formOptions?.volumeOptions || []).map((o, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1"><I18nInput value={o} onChange={(v) => {
                  const arr = [...(data.formOptions.volumeOptions || [])];
                  arr[i] = v;
                  setData({ ...data, formOptions: { ...data.formOptions, volumeOptions: arr } });
                }} /></div>
                <button onClick={() => {
                  const arr = (data.formOptions.volumeOptions || []).filter((_, j) => j !== i);
                  setData({ ...data, formOptions: { ...data.formOptions, volumeOptions: arr } });
                }} className="p-2 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setData({ ...data, formOptions: { ...data.formOptions, volumeOptions: [...(data.formOptions?.volumeOptions || []), { es: '', en: '' }] } })} className="inline-flex items-center gap-2 px-4 h-9 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white text-xs transition-colors">
              <Plus className="w-3 h-3" /> Añadir opción
            </button>
          </div>
          <SaveBar saving={saving} onSave={() => save({ formOptions: data.formOptions })} />
        </div>
      )}

      {/* LEGAL DATA - sin traducción */}
      {tab === 'legal' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <SectionTitle sub="Estos datos se usan automáticamente en las páginas Aviso Legal, Privacidad y Cookies (ES y EN).">
            Datos Legales de la Empresa
          </SectionTitle>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Razón social *"><input className="ldc-input" placeholder="Euterpe Films, S.L." value={data.legal?.legalName || ''} onChange={(e) => setLegal('legalName', e.target.value)} /></Field>
            <Field label="CIF / NIF *"><input className="ldc-input" placeholder="B-12345678" value={data.legal?.cif || ''} onChange={(e) => setLegal('cif', e.target.value)} /></Field>
            <Field label="Domicilio social *" full><input className="ldc-input" placeholder="Calle Ejemplo 1, 28013 Madrid" value={data.legal?.address || ''} onChange={(e) => setLegal('address', e.target.value)} /></Field>
            <Field label="Email legal / LSSI"><input className="ldc-input" placeholder="info@euterpefilms.com" value={data.legal?.email || ''} onChange={(e) => setLegal('email', e.target.value)} /></Field>
            <Field label="Teléfono"><input className="ldc-input" placeholder="+34 911 234 567" value={data.legal?.phone || ''} onChange={(e) => setLegal('phone', e.target.value)} /></Field>
            <Field label="Email del DPO" full><input className="ldc-input" placeholder="dpo@euterpefilms.com" value={data.legal?.dpoEmail || ''} onChange={(e) => setLegal('dpoEmail', e.target.value)} /></Field>
            <Field label="Registro Mercantil" full><input className="ldc-input" placeholder="Tomo 0000, Folio 00, Hoja M-000000, inscripción 1ª" value={data.legal?.registroMercantil || ''} onChange={(e) => setLegal('registroMercantil', e.target.value)} /></Field>
            <Field label="URL del sitio web"><input className="ldc-input" placeholder="https://euterpefilms.com" value={data.legal?.websiteUrl || ''} onChange={(e) => setLegal('websiteUrl', e.target.value)} /></Field>
            <Field label="Última actualización"><input className="ldc-input" placeholder="Junio 2026" value={data.legal?.lastUpdate || ''} onChange={(e) => setLegal('lastUpdate', e.target.value)} /></Field>
          </div>
          <SaveBar saving={saving} onSave={() => save({ legal: data.legal })} />
        </div>
      )}

      {/* NOTIFICATIONS - email de leads */}
      {tab === 'notifications' && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center">
              <Mail className="w-4 h-4 text-[#00e0c7]" />
            </div>
            <div>
              <h2 className="font-display text-white text-2xl font-light">Notificaciones por email</h2>
              <p className="text-white/40 text-sm mt-0.5">Postmark · cada nuevo lead llega a tu bandeja de entrada.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <Field label="Email destinatario de los leads *" full>
              <input
                className="ldc-input"
                type="email"
                placeholder="comercial@euterpefilms.com"
                value={data.notifications?.leadsRecipient || ''}
                onChange={(e) => setNotif('leadsRecipient', e.target.value)}
              />
              <span className="block text-[11px] text-white/40 mt-1.5">
                A esta dirección llegará un email cada vez que alguien rellene el formulario de contacto.
              </span>
            </Field>

            <label className="block sm:col-span-2 cursor-pointer">
              <div className="flex items-center justify-between p-4 rounded-xl border border-white/8 bg-white/[0.02] hover:border-white/15 transition-colors">
                <div>
                  <div className="text-white text-sm font-medium mb-1">Envío de notificaciones activado</div>
                  <div className="text-white/45 text-[12px]">
                    Si lo desactivas, los leads se siguen guardando en la base de datos pero no recibirás emails.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={data.notifications?.enabled !== false}
                  onChange={(e) => setNotif('enabled', e.target.checked)}
                  className="w-5 h-5 accent-[#00e0c7]"
                />
              </div>
            </label>
          </div>

          <div className="p-4 rounded-xl border border-[#00e0c7]/20 bg-[#00e0c7]/[0.04] mb-6">
            <p className="text-[12px] text-[#9be8dc] leading-relaxed">
              💡 <strong>Probar la integración:</strong> guarda primero los cambios y luego pulsa "Enviar email de prueba" para verificar que el mensaje llega correctamente a tu bandeja.
            </p>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-white/8">
            <button
              onClick={sendTestEmail}
              disabled={sendingTest || !data.notifications?.leadsRecipient}
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full border border-[#00e0c7]/40 bg-[#00e0c7]/10 hover:bg-[#00e0c7]/20 text-[#9be8dc] font-medium text-sm transition-colors disabled:opacity-50"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar email de prueba
            </button>
            <button
              onClick={() => save({ notifications: data.notifications })}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold text-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar cambios
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // helpers nested in component (use data)
  function updateArrItem(key, index, field, value) {
    const arr = [...(data[key] || [])];
    arr[index] = { ...arr[index], [field]: value };
    setData({ ...data, [key]: arr });
  }
  function addArrItem(key, template) {
    setData({ ...data, [key]: [...(data[key] || []), template] });
  }
  function removeArrItem(key, index) {
    setData({ ...data, [key]: (data[key] || []).filter((_, i) => i !== index) });
  }
  function updatePillar(i, field, value) {
    const arr = [...(data.about.pillars || [])];
    arr[i] = { ...arr[i], [field]: value };
    setAbout('pillars', arr);
  }
};

const SaveBar = ({ saving, onSave }) => (
  <div className="mt-8 pt-6 border-t border-white/8 flex justify-end">
    <button
      onClick={onSave}
      disabled={saving}
      className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold text-sm transition-colors disabled:opacity-50"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      Guardar cambios
    </button>
  </div>
);

const TranslateSectionBtn = ({ translating, onClick }) => (
  <button
    onClick={onClick}
    disabled={translating}
    className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/10 text-[#9be8dc] hover:bg-[#00e0c7]/20 text-xs font-medium transition-colors disabled:opacity-50"
    title="Traducir todos los campos vacíos en EN"
  >
    {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
    Traducir sección a EN
  </button>
);

const ItemCard = ({ children, index, onRemove }) => (
  <div className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-widest text-white/40">Ítem #{index + 1}</span>
      <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
    </div>
    {children}
  </div>
);

const AddBtn = ({ onClick }) => (
  <button onClick={onClick} className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white hover:border-[#00e0c7]/40 text-sm transition-colors">
    <Plus className="w-4 h-4" /> Añadir ítem
  </button>
);

const BulletsEditor = ({ bullets, onChange }) => {
  const update = (i, v) => {
    const arr = [...bullets];
    arr[i] = v;
    onChange(arr);
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Bullets</div>
      <div className="space-y-2">
        {bullets.map((b, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1"><I18nInput value={b} onChange={(v) => update(i, v)} /></div>
            <button onClick={() => onChange(bullets.filter((_, j) => j !== i))} className="p-2 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => onChange([...bullets, { es: '', en: '' }])} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-dashed border-white/15 text-white/60 hover:text-white text-[11px] transition-colors">
          <Plus className="w-3 h-3" /> Añadir bullet
        </button>
      </div>
    </div>
  );
};

export default AdminContent;
